import { useState, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import SuiBox from "components/SuiBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import routes from "routes";
import { useSoftUIController, setMiniSidenav, setOpenConfigurator } from "context";
import { useDispatch, useSelector } from "react-redux";
import PrivateRoute from "routes/PrivateRoute";
import PublicRoute from "routes/PublicRoute";
import PageNotFound from "layouts/PageNotFound";

// Images
import brand from "assets/images/haazri-logo.png";
import { NetworkConfig } from "./services/apiConfig";
import { fetchMe } from "layouts/authentication/auth.thunk";

NetworkConfig();
export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const reduxDispatch = useDispatch();
  const { isLoggedIn, token, admin } = useSelector((state) => state.auth);

  // Refresh role/permissions on load so role changes apply without re-login.
  useEffect(() => {
    if (isLoggedIn && token && admin?.id) {
      reduxDispatch(fetchMe(admin.id));
    }
  }, [isLoggedIn, token, admin?.id]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.nested) {
        const parentPath = route.route.replace(/^\//, "") || "/";
        const parentElement =
          route.privacy === "private" ? (
            <PrivateRoute route={route}>{route.component}</PrivateRoute>
          ) : (
            <PublicRoute>{route.component}</PublicRoute>
          );
        return (
          <Route path={parentPath} element={parentElement} key={route.key}>
            {route.nested.map((child) => (
              <Route
                path={child.path}
                element={<PrivateRoute route={child}>{child.component}</PrivateRoute>}
                key={child.key}
              />
            ))}
          </Route>
        );
      }

      if (route.privacy === "private") {
        return (
          <Route
            exact
            path={route.route}
            element={<PrivateRoute route={route}>{route.component}</PrivateRoute>}
            key={route.key}
          />
        );
      } else if (route.privacy === "oauth") {
        // No PrivateRoute/PublicRoute wrapper — see routes.js's comment on
        // this route. The page itself handles both login states.
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      } else {
        return (
          <Route
            exact
            path={route.route}
            element={<PublicRoute>{route.component}</PublicRoute>}
            key={route.key}
          />
        );
      }
    });

  const configsButton = (
    <SuiBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="default" color="inherit">
        settings
      </Icon>
    </SuiBox>
  );
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brand}
            brandName="Haazri Admin Portal"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          {/* <Configurator /> */}
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route
          path="*"
          element={
            <PrivateRoute>
              <PageNotFound />
            </PrivateRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}
